import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import lessonsProgressService from '../services/lessonsProgressService';
import PracticeQuiz from '../components/PracticeQuiz';

// SVG Components for Physics Diagrams
const FrictionDiagram = () => (
  <svg viewBox="0 0 400 200" className="w-full max-w-md mx-auto">
    {/* Surface */}
    <rect x="50" y="140" width="300" height="20" fill="#8B4513" stroke="#654321" strokeWidth="2"/>

    {/* Block */}
    <rect x="150" y="100" width="80" height="40" fill="#4A90E2" stroke="#2E5C8A" strokeWidth="2"/>

    {/* Weight arrow (downward) */}
    <line x1="190" y1="100" x2="190" y2="60" stroke="#E74C3C" strokeWidth="3" markerEnd="url(#arrowhead-red)"/>
    <text x="200" y="75" fill="#E74C3C" fontSize="12" fontWeight="bold">Weight</text>

    {/* Normal force arrow (upward) */}
    <line x1="160" y1="100" x2="160" y2="60" stroke="#27AE60" strokeWidth="3" markerEnd="url(#arrowhead-green)"/>
    <text x="120" y="75" fill="#27AE60" fontSize="12" fontWeight="bold">Normal</text>

    {/* Applied force arrow (right) */}
    <line x1="230" y1="120" x2="290" y2="120" stroke="#9B59B6" strokeWidth="3" markerEnd="url(#arrowhead-purple)"/>
    <text x="295" y="115" fill="#9B59B6" fontSize="12" fontWeight="bold">Applied Force</text>

    {/* Friction force arrow (left) */}
    <line x1="150" y1="120" x2="90" y2="120" stroke="#F39C12" strokeWidth="3" markerEnd="url(#arrowhead-orange)"/>
    <text x="20" y="115" fill="#F39C12" fontSize="12" fontWeight="bold">Friction</text>

    {/* Arrow markers */}
    <defs>
      <marker id="arrowhead-red" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
        <polygon points="0 0, 10 3.5, 0 7" fill="#E74C3C"/>
      </marker>
      <marker id="arrowhead-green" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
        <polygon points="0 0, 10 3.5, 0 7" fill="#27AE60"/>
      </marker>
      <marker id="arrowhead-purple" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
        <polygon points="0 0, 10 3.5, 0 7" fill="#9B59B6"/>
      </marker>
      <marker id="arrowhead-orange" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
        <polygon points="0 0, 10 3.5, 0 7" fill="#F39C12"/>
      </marker>
    </defs>
  </svg>
);

const CarFrictionDiagram = () => (
  <svg viewBox="0 0 400 200" className="w-full max-w-md mx-auto">
    {/* Car body */}
    <rect x="120" y="100" width="120" height="40" rx="5" fill="#E74C3C" stroke="#C0392B" strokeWidth="2"/>

    {/* Car windows */}
    <rect x="130" y="90" width="100" height="15" rx="3" fill="#AED6F1" stroke="#3498DB" strokeWidth="1"/>

    {/* Wheels */}
    <circle cx="140" cy="150" r="15" fill="#2C3E50" stroke="#1B2631" strokeWidth="2"/>
    <circle cx="220" cy="150" r="15" fill="#2C3E50" stroke="#1B2631" strokeWidth="2"/>

    {/* Ground */}
    <line x1="50" y1="165" x2="350" y2="165" stroke="#8B4513" strokeWidth="4"/>

    {/* Thrust arrow (right) */}
    <line x1="240" y1="120" x2="300" y2="120" stroke="#27AE60" strokeWidth="3" markerEnd="url(#arrowhead-green)"/>
    <text x="305" y="115" fill="#27AE60" fontSize="12" fontWeight="bold">Thrust</text>

    {/* Friction arrow (left) */}
    <line x1="120" y1="120" x2="60" y2="120" stroke="#F39C12" strokeWidth="3" markerEnd="url(#arrowhead-orange)"/>
    <text x="10" y="115" fill="#F39C12" fontSize="12" fontWeight="bold">Friction</text>

    {/* Arrow markers (reuse from previous diagram) */}
    <defs>
      <marker id="arrowhead-green" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
        <polygon points="0 0, 10 3.5, 0 7" fill="#27AE60"/>
      </marker>
      <marker id="arrowhead-orange" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
        <polygon points="0 0, 10 3.5, 0 7" fill="#F39C12"/>
      </marker>
    </defs>
  </svg>
);

const AirResistanceDiagram = () => (
  <svg viewBox="0 0 400 200" className="w-full max-w-md mx-auto">
    {/* Car body - more aerodynamic */}
    <path d="M120 120 L240 120 L250 130 L240 140 L120 140 L110 130 Z" fill="#3498DB" stroke="#2980B9" strokeWidth="2"/>

    {/* Car windows */}
    <path d="M130 110 L230 110 L235 120 L125 120 Z" fill="#AED6F1" stroke="#3498DB" strokeWidth="1"/>

    {/* Wheels */}
    <circle cx="140" cy="150" r="12" fill="#2C3E50" stroke="#1B2631" strokeWidth="2"/>
    <circle cx="220" cy="150" r="12" fill="#2C3E50" stroke="#1B2631" strokeWidth="2"/>

    {/* Ground */}
    <line x1="50" y1="162" x2="350" y2="162" stroke="#8B4513" strokeWidth="3"/>

    {/* Thrust arrow (right) */}
    <line x1="250" y1="130" x2="310" y2="130" stroke="#27AE60" strokeWidth="3" markerEnd="url(#arrowhead-green)"/>
    <text x="315" y="125" fill="#27AE60" fontSize="12" fontWeight="bold">Thrust</text>

    {/* Air resistance arrow (left) */}
    <line x1="110" y1="130" x2="50" y2="130" stroke="#E67E22" strokeWidth="3" markerEnd="url(#arrowhead-orange)"/>
    <text x="10" y="125" fill="#E67E22" fontSize="11" fontWeight="bold">Air Resistance</text>

    {/* Air flow lines */}
    <path d="M80 100 Q100 110 80 120" stroke="#95A5A6" strokeWidth="2" fill="none"/>
    <path d="M80 140 Q100 150 80 160" stroke="#95A5A6" strokeWidth="2" fill="none"/>

    {/* Arrow markers */}
    <defs>
      <marker id="arrowhead-green" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
        <polygon points="0 0, 10 3.5, 0 7" fill="#27AE60"/>
      </marker>
      <marker id="arrowhead-orange" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
        <polygon points="0 0, 10 3.5, 0 7" fill="#E67E22"/>
      </marker>
    </defs>
  </svg>
);

// Mock lesson data
const lessonData = {
  'physics-2-5': {
    title: '2.5 - Friction',
    subject: 'Physics',
    duration: 5,
    slides: [
      {
        id: 1,
        title: 'What is friction?',
        content: `Friction is a force that opposes the relative motion between two surfaces that are in contact.

For example, when you try to move an object across a surface, friction pushes against the object in the opposite direction to its movement.`,
        diagram: 'friction',
        points: [
          'Friction always opposes motion',
          'It acts between surfaces in contact',
          'Direction is always opposite to movement'
        ]
      },
      {
        id: 2,
        title: 'Advantages of friction',
        content: `Friction has many important advantages in everyday life:`,
        points: [
          {
            title: 'Friction allows motion to start and stop',
            description: 'The friction between tyres and the road surface allows for acceleration, braking, and changing direction. Without this friction, the tyres would slip.'
          },
          {
            title: 'Friction prevents slipping',
            description: 'The friction between our hands and an object allows us to grip things. For example, the friction between our hands and the handlebars on a bike lets us hold on while riding.'
          }
        ]
      },
      {
        id: 3,
        title: 'Disadvantages of friction',
        content: `While friction can be helpful, it also has some disadvantages:`,
        diagram: 'car-friction',
        points: [
          {
            title: 'Friction wastes energy',
            description: 'Friction converts useful energy into heat energy which is wasted. The heat generated in tyres is an example of this energy loss.',
            diagram: 'car-friction'
          },
          {
            title: 'Friction limits top speed',
            description: 'At high speeds, air resistance is a form of friction which takes a lot of energy to overcome, limiting top speeds. Aerodynamic shaping aims to minimise this frictional force.',
            diagram: 'air-resistance'
          }
        ]
      }
    ]
  }
};

export default function LessonViewer() {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [lessonProgress, setLessonProgress] = useState(null);
  const [isCompleted, setIsCompleted] = useState(false);
  const [showPracticeQuiz, setShowPracticeQuiz] = useState(false);

  const lesson = lessonData[lessonId];

  // Load progress and initialize lesson tracking
  useEffect(() => {
    if (lessonId) {
      // Load existing progress
      const progress = lessonsProgressService.getLessonProgress(lessonId);
      setLessonProgress(progress);
      setCurrentSlide(progress.currentSlide || 0);
      setIsCompleted(progress.completed || false);

      // Mark lesson as started if it's the first time
      if (progress.progress === 0) {
        lessonsProgressService.startLesson(lessonId);
      }
    }
  }, [lessonId]);

  // Update progress when slide changes
  useEffect(() => {
    if (lessonId && lesson) {
      const updatedProgress = lessonsProgressService.updateSlideProgress(
        lessonId,
        currentSlide,
        lesson.slides.length
      );
      setLessonProgress(updatedProgress);
    }
  }, [currentSlide, lessonId, lesson]);

  // Complete lesson handler
  const handleCompleteLesson = () => {
    if (lessonId) {
      const updatedProgress = lessonsProgressService.completeLesson(lessonId);
      setLessonProgress(updatedProgress);
      setIsCompleted(true);
    }
  };

  // Handler for practice quiz - show the practice quiz modal
  const handleTakePracticeQuiz = () => {
    setShowPracticeQuiz(true);
  };

  // Handler for creating assignment - navigate to assignment manager
  const handleCreateAssignment = () => {
    // Navigate to assignment manager with lesson context
    navigate('/teacher/assignments', {
      state: {
        fromLesson: true,
        lessonId: lessonId,
        lessonTitle: lesson.title,
        subject: lesson.subject
      }
    });
  };

  if (!lesson) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Lesson not found</h2>
          <Link to="/lessons" className="text-blue-600 hover:text-blue-800">
            ← Back to lessons
          </Link>
        </div>
      </div>
    );
  }

  const slide = lesson.slides[currentSlide];
  const isFirstSlide = currentSlide === 0;
  const isLastSlide = currentSlide === lesson.slides.length - 1;

  const renderDiagram = (diagramType) => {
    switch (diagramType) {
      case 'friction':
        return <FrictionDiagram />;
      case 'car-friction':
        return <CarFrictionDiagram />;
      case 'air-resistance':
        return <AirResistanceDiagram />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link
                to="/lessons"
                className="text-gray-500 hover:text-gray-700 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </Link>
              <div>
                <h1 className="text-xl font-bold text-gray-900">{lesson.title}</h1>
                <p className="text-sm text-gray-600">{lesson.subject} • {lesson.duration} mins</p>
              </div>
            </div>

            {/* Progress indicator */}
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-600">
                <div>{currentSlide + 1} of {lesson.slides.length}</div>
                {lessonProgress && (
                  <div className="text-xs text-gray-500">
                    {lessonProgress.progress}% complete
                    {isCompleted && <span className="text-green-600 ml-2">✅ Done</span>}
                  </div>
                )}
              </div>
              <div className="w-32 bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${lessonProgress ? lessonProgress.progress : 0}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">

          {/* Slide Header */}
          <div className="bg-blue-50 px-6 py-4 border-b border-blue-100">
            <h2 className="text-2xl font-bold text-blue-900">{slide.title}</h2>
          </div>

          {/* Slide Content */}
          <div className="p-6">
            {/* Main content */}
            {slide.content && (
              <div className="mb-6">
                <p className="text-gray-700 text-lg leading-relaxed whitespace-pre-line">
                  {slide.content}
                </p>
              </div>
            )}

            {/* Diagram */}
            {slide.diagram && (
              <div className="mb-6 bg-gray-50 rounded-lg p-6">
                <div className="text-center">
                  {renderDiagram(slide.diagram)}
                  <p className="text-sm text-gray-600 mt-4 italic">
                    Interactive diagram showing forces and movement directions
                  </p>
                </div>
              </div>
            )}

            {/* Points/List */}
            {slide.points && (
              <div className="space-y-4">
                {slide.points.map((point, index) => (
                  <div key={index}>
                    {typeof point === 'string' ? (
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mt-0.5">
                          <span className="text-blue-600 text-sm font-bold">{index + 1}</span>
                        </div>
                        <p className="text-gray-700">{point}</p>
                      </div>
                    ) : (
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h3 className="font-semibold text-gray-900 mb-2">
                          {index + 1}. {point.title}
                        </h3>
                        <p className="text-gray-700 mb-3">{point.description}</p>

                        {point.diagram && (
                          <div className="mt-4 bg-white rounded-lg p-4">
                            {renderDiagram(point.diagram)}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Navigation */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <button
                onClick={() => setCurrentSlide(Math.max(0, currentSlide - 1))}
                disabled={isFirstSlide}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                  isFirstSlide
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span>Previous</span>
              </button>

              <div className="flex space-x-2">
                {lesson.slides.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`w-3 h-3 rounded-full transition-colors ${
                      index === currentSlide ? 'bg-blue-500' : 'bg-gray-300'
                    }`}
                  />
                ))}
              </div>

              {isLastSlide ? (
                <div className="flex space-x-3">
                  <button
                    onClick={handleCompleteLesson}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                      isCompleted
                        ? 'bg-green-100 text-green-800 border border-green-300'
                        : 'bg-green-600 text-white hover:bg-green-700'
                    }`}
                  >
                    <span>{isCompleted ? 'Lesson Completed!' : 'Complete Lesson'}</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </button>
                  <Link
                    to="/lessons/physics-2-6"
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
                  >
                    <span>Next Lesson</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                </div>
              ) : (
                <button
                  onClick={() => setCurrentSlide(Math.min(lesson.slides.length - 1, currentSlide + 1))}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
                >
                  <span>Next</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Lesson Actions */}
        <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Test Your Knowledge</h3>
          <div className="flex space-x-4">
            <button
              onClick={handleTakePracticeQuiz}
              className="flex-1 bg-purple-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-purple-700 transition-colors"
              title="Generate a practice quiz based on this lesson"
            >
              🧠 Take Practice Quiz
            </button>
            <button
              onClick={handleCreateAssignment}
              className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors"
              title="Create an assignment for students based on this lesson"
            >
              📝 Create Assignment
            </button>
          </div>
        </div>

        {/* Helpful feedback */}
        <div className="mt-6 bg-blue-50 rounded-lg p-4 border border-blue-200">
          <h4 className="font-medium text-blue-900 mb-2">💡 Is this lesson helpful?</h4>
          <div className="flex space-x-3">
            <button className="px-3 py-1 bg-white text-blue-700 rounded border border-blue-300 hover:bg-blue-50 text-sm">
              👍 Yes
            </button>
            <button className="px-3 py-1 bg-white text-blue-700 rounded border border-blue-300 hover:bg-blue-50 text-sm">
              👎 No
            </button>
            <button className="px-3 py-1 bg-white text-blue-700 rounded border border-blue-300 hover:bg-blue-50 text-sm">
              💬 Suggest improvement
            </button>
          </div>
        </div>
      </div>

      {/* Practice Quiz Modal */}
      {showPracticeQuiz && (
        <PracticeQuiz
          lessonId={lessonId}
          lessonTitle={lesson.title}
          subject={lesson.subject}
          onClose={() => setShowPracticeQuiz(false)}
        />
      )}
    </div>
  );
}